// @ts-nocheck

import React, { useEffect, useState } from "react";
import DashboardLayout from "../../components/layoutContainers/dashboardLayout";
import SoftBox from "../../components/common/softBox";
import DashboardNavbar from "../../components/navbars/dashboardNavbar";
import SoftTypography from "../../components/common/softTypography";
import SoftInput from "../../components/common/softInput";
import SoftButton from "../../components/common/softButton";
import {
  Card,
  Grid,
  IconButton,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAppDispatch, useAppSelector } from "../../Hooks";
import { getSapConfig, updateSapConfig } from "../../features/sapConfig";
import { getSapCompaniesQuery } from "../../api/sapConfigApi";
import { toast } from "react-toastify";

const codeOf = (company: any) =>
  company?.companyCode ||
  company?.CompanyCode ||
  company?.BusinessPartner ||
  "";

const nameOf = (company: any) =>
  company?.companyName ||
  company?.CompanyName ||
  company?.BusinessPartnerFullName ||
  company?.PartnerName ||
  "";

const normalizeCompany = (company: any) => ({
  companyCode: codeOf(company),
  companyName: nameOf(company),
  legalEmail: company?.legalEmail || "",
  commercialEmail: company?.commercialEmail || "",
});

const SapCompanies: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data } = useAppSelector((state: any) => state.sapConfig);

  const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
  const [configCompanies, setConfigCompanies] = useState<any[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [details, setDetails] = useState<{
    [code: string]: { legalEmail: string; commercialEmail: string };
  }>({});
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const handleFetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const res = await getSapCompaniesQuery();
      let list: any[] = [];
      if (Array.isArray(res?.data)) {
        list = res.data;
      } else if (Array.isArray(res?.data?.d?.results)) {
        list = res.data.d.results;
      } else if (Array.isArray(res?.d?.results)) {
        list = res.d.results;
      }
      setAvailableCompanies(list.map(normalizeCompany));
      if (list.length === 0) {
        toast.info("No companies returned from SAP.");
      }
    } catch (err) {
      toast.error("Failed to fetch companies from SAP.");
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    dispatch(getSapConfig());
    handleFetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      const saved = Array.isArray(data.selected_companies)
        ? data.selected_companies.map(normalizeCompany)
        : [];
      setConfigCompanies(saved);
      setSelectedCodes(saved.map((c) => c.companyCode).filter(Boolean));
      const map: {
        [code: string]: { legalEmail: string; commercialEmail: string };
      } = {};
      saved.forEach((c) => {
        if (c.companyCode) {
          map[c.companyCode] = {
            legalEmail: c.legalEmail || "",
            commercialEmail: c.commercialEmail || "",
          };
        }
      });
      setDetails(map);
    }
  }, [data]);

  const nameFor = (code: string) =>
    availableCompanies.find((c) => c.companyCode === code)?.companyName ||
    configCompanies.find((c) => c.companyCode === code)?.companyName ||
    code;

  const handleSelectChange = (event: any) => {
    const value = event.target.value;
    const codes: string[] = typeof value === "string" ? value.split(",") : value;
    setSelectedCodes(codes);
    setDetails((prev) => {
      const next = { ...prev };
      codes.forEach((code) => {
        if (!next[code]) {
          next[code] = { legalEmail: "", commercialEmail: "" };
        }
      });
      return next;
    });
  };

  const handleDetailChange = (
    code: string,
    field: "legalEmail" | "commercialEmail",
    value: string
  ) => {
    setDetails((prev) => ({
      ...prev,
      [code]: { ...prev[code], [field]: value },
    }));
  };

  const handleRemove = (code: string) => {
    setSelectedCodes((prev) => prev.filter((c) => c !== code));
    if (editingCode === code) {
      setEditingCode(null);
    }
  };

  const handleSave = () => {
    if (!data) {
      toast.error("SAP configuration not loaded.");
      return;
    }
    const valid = selectedCodes.filter(Boolean);
    if (valid.length !== selectedCodes.length) {
      toast.error(
        "Every company needs a Company Code before saving. Remove rows without a code."
      );
      return;
    }
    const selected_companies = valid.map((code) => ({
      companyCode: code,
      companyName: nameFor(code),
      legalEmail: details[code]?.legalEmail || "",
      commercialEmail: details[code]?.commercialEmail || "",
    }));
    dispatch(
      updateSapConfig({
        data: { ...data, selected_companies },
      })
    ).then((resultAction) => {
      if (updateSapConfig.rejected.match(resultAction)) {
        toast.error("Failed to update SAP companies.");
      } else {
        toast.success("SAP companies updated successfully.");
      }
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftBox py={3}>
        <Card>
          <SoftBox p={3}>
            <SoftBox mb={2}>
              <SoftTypography variant="h4" fontWeight="bold">
                SAP Company List
              </SoftTypography>
            </SoftBox>

            <SoftBox mb={3}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  SAP System Companies
                </SoftTypography>
              </SoftBox>
              <FormControl fullWidth size="small">
                <InputLabel id="company-label">Company</InputLabel>
                <Select
                  multiple
                  labelId="company-label"
                  id="Company"
                  name="selected_companies"
                  value={selectedCodes}
                  onChange={handleSelectChange}
                  label="Company"
                  renderValue={(selected) =>
                    (selected as string[]).map(nameFor).join(", ")
                  }
                >
                  {availableCompanies.length > 0 ? (
                    availableCompanies.map((company, index) => (
                      <MenuItem
                        value={company.companyCode}
                        key={`company_${company.companyCode}_${index}`}
                      >
                        <Checkbox
                          checked={selectedCodes.indexOf(company.companyCode) > -1}
                        />
                        <ListItemText
                          primary={company.companyName || company.companyCode}
                        />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      {loadingCompanies
                        ? "Loading SAP companies..."
                        : "No companies loaded from SAP."}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </SoftBox>

            <SoftBox mt={3}>
              <SoftButton
                variant="gradient"
                color="final"
                fullWidth
                onClick={handleSave}
              >
                SAVE SAP COMPANIES
              </SoftButton>
            </SoftBox>

            <SoftBox mt={4} mb={1}>
              <SoftTypography variant="h6" fontWeight="bold">
                Selected SAP Companies to User
              </SoftTypography>
            </SoftBox>
            {selectedCodes.length > 0 ? (
              <SoftBox>
                {selectedCodes.map((code, index) => (
                  <SoftBox
                    key={`selected-company-${code}-${index}`}
                    sx={{
                      borderBottom: "1px solid #eee",
                      py: 1.5,
                    }}
                  >
                    <Grid container alignItems="center" justifyContent="space-between">
                      <Grid item xs>
                        <SoftTypography variant="button" fontWeight="medium">
                          {nameFor(code)}
                          {` (${code})`}
                        </SoftTypography>
                      </Grid>
                      <Grid item>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setEditingCode(editingCode === code ? null : code)
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(code)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>

                    {editingCode === code && (
                      <Grid container spacing={2} mt={0.5}>
                        <Grid item xs={12} sm={6}>
                          <SoftBox mb={1} ml={0.5}>
                            <SoftTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Legal Email
                            </SoftTypography>
                          </SoftBox>
                          <SoftInput
                            type="email"
                            placeholder="legal@company.com"
                            value={details[code]?.legalEmail || ""}
                            onChange={(e) =>
                              handleDetailChange(
                                code,
                                "legalEmail",
                                e.target.value
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <SoftBox mb={1} ml={0.5}>
                            <SoftTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Commercial / Credit Email
                            </SoftTypography>
                          </SoftBox>
                          <SoftInput
                            type="email"
                            placeholder="credit@company.com"
                            value={details[code]?.commercialEmail || ""}
                            onChange={(e) =>
                              handleDetailChange(
                                code,
                                "commercialEmail",
                                e.target.value
                              )
                            }
                          />
                        </Grid>
                      </Grid>
                    )}
                  </SoftBox>
                ))}
              </SoftBox>
            ) : (
              <SoftTypography variant="button" color="text">
                No companies selected.
              </SoftTypography>
            )}
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
};

export default SapCompanies;
